// localStorage layer for refund-session resume markers. See ../README.md § Local resume.
export type RefundMethodValue = string

// Per-(order,rx) marker:
//  • absent             → first Get help → selection modal → open NEW conversation.
//  • present, no convId → draft (quick-reply not pressed) → selection modal again.
//  • present + convId   → submitted → reopen the exact conversation.
export interface RefundSessionMarker {
  orderGuid: string
  rxGuid: string
  method?: RefundMethodValue
  conversationId?: string
  sent?: boolean
  userId?: string
}

const REFUND_SESSION_KEY = 'tds.revere.refundSession'

// Keyed per order so concurrent in-flight refunds (multiple tabs) don't clobber each other. This
// isolates our marker only — Intercom's user attribute still races. See ../README.md § Known
// limitation.
const markerKey = (orderGuid: string, rxGuid: string) =>
  `${orderGuid}::${rxGuid}`

export const readMarkerMap = (): Record<string, RefundSessionMarker> => {
  try {
    const raw = localStorage.getItem(REFUND_SESSION_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    // Migrate an older single-marker shape into the map.
    if (typeof parsed.orderGuid === 'string') {
      return {
        [markerKey(parsed.orderGuid, parsed.rxGuid)]:
          parsed as RefundSessionMarker
      }
    }
    return parsed as Record<string, RefundSessionMarker>
  } catch {
    return {}
  }
}

export const readMarker = (
  orderGuid?: string,
  rxGuid?: string
): RefundSessionMarker | null => {
  if (!orderGuid || !rxGuid) return null
  return readMarkerMap()[markerKey(orderGuid, rxGuid)] ?? null
}

export const writeMarker = (marker: RefundSessionMarker) => {
  try {
    const map = readMarkerMap()
    map[markerKey(marker.orderGuid, marker.rxGuid)] = marker
    localStorage.setItem(REFUND_SESSION_KEY, JSON.stringify(map))
  } catch {
    // private mode — resume is a nicety.
  }
}

// Clear only this order's entry — never the whole map (other orders may be in flight).
export const clearMarker = (orderGuid: string, rxGuid: string) => {
  try {
    const map = readMarkerMap()
    delete map[markerKey(orderGuid, rxGuid)]
    localStorage.setItem(REFUND_SESSION_KEY, JSON.stringify(map))
  } catch {
    // no-op
  }
}
